import React, { useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
import { CampaignList } from '../components/CampaignList';
import { Campaign } from '../../../shared/types/Campaign';
// Mock campaign data based on screenshots
const mockCampaigns: Campaign[] = [{
  id: 'BILASHOP2025',
  title: '[BILA Mart] 7-days Promotion for Shop',
  description: '[BILA Mart] 7-days Promotion for Shop',
  expiryDate: '07/03/2026',
  discountPercentage: 0,
  fixedDiscountAmount: 50,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 5000,
  budgetUsed: 0,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BILASHOP2025',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 50,
  maxDiscount: 50,
  status: 'active',
  companies: ['CIMB Life'],
  transactionVolume: 47250,
  transactionCount: 63
}, {
  id: 'BILA10MYR',
  title: `BILA Mart Special Package (${getTenant().currency.code} 10 Discount)`,
  description: 'DISCOUNT_COUPON',
  expiryDate: '02/01/2026',
  discountPercentage: 0,
  fixedDiscountAmount: 10,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 11000,
  budgetUsed: 10960,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BILA10MYR',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 10,
  maxDiscount: 10,
  status: 'active',
  transactionVolume: 1284500,
  transactionCount: 1096
}, {
  id: 'BULK2025',
  title: 'Summer Festival Bulk Codes',
  description: 'Unique codes for summer festival attendees',
  startDate: '01/11/2025',
  expiryDate: '31/01/2026',
  discountPercentage: 15,
  fixedDiscountAmount: 0,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budgetType: 'fixed',
  budget: 50000,
  budgetUsed: 12500,
  strictlyEnforceBudget: true,
  paymentMethods: ['Card', 'PromptPay'],
  cardTargetingMode: 'all',
  cardBrands: [],
  cardBin: '',
  cardIssuingCountry: '',
  cardType: 'All Types',
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Bulk',
  minTransactionValue: 500,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 1000,
  status: 'active',
  transactionVolume: 312750,
  transactionCount: 245
}];
export function Campaigns() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const handleCreateCampaign = () => {
    navigate('/campaign-form');
  };
  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign-details?id=${campaignId}`);
  };
  const filteredCampaigns = mockCampaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.id.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Calculate summary metrics
  const activeCampaigns = mockCampaigns.filter(c => c.status === 'active');
  const totalBudget = activeCampaigns.reduce((sum, c) => sum + c.budget, 0);
  const totalUsed = activeCampaigns.reduce((sum, c) => sum + c.budgetUsed, 0);
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-12055" data-mp="2dx8clr55ojbr">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-12056" data-mp="1g8y895hwdjhh">
        <div data-id="element-12057" data-mp="1gaj1mrsh6wdr">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12058" data-mp="1gb2kqbtv3qtd">
            {t('pages.Campaigns.campaigns')}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-12059" data-mp="1q75n9ismlrgu">
            {t('pages.Campaigns.manage_your_discount_codes_and_promotional')}
          </p>
        </div>
        <button onClick={handleCreateCampaign} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12060" data-mp="1uaakp6xun2ym">
          {t('pages.Campaigns.create_campaign')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12061" data-mp="tzrdf42uj96x">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-12062" data-mp="pypfqrokv572">
          <div className="flex items-center gap-3 mb-2" data-id="element-12063" data-mp="l7pfbcg924zt">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg" data-id="element-12064" data-mp="l64mbr9l2u42">
              <TagIcon size={20} data-id="element-12065" data-mp="g8cwd2si1i2i" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-12066" data-mp="ba1i8fv68leh">
              {t('pages.Campaigns.active_campaigns')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-12067" data-mp="69exp71d085s">
            {activeCampaigns.length}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-12068" data-mp="2b7sn9g9cvaf">
          <div className="flex items-center gap-3 mb-2" data-id="element-12069" data-mp="3thiqkwpz1fxj">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg" data-id="element-12070" data-mp="1wnnwy4phtzcn">
              <PercentIcon size={20} data-id="element-12071" data-mp="1rokosbe7vd0g" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-12072" data-mp="1roujude4cad0">
              {t('pages.Campaigns.total_redemptions')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-12073" data-mp="2fk67sgu9w4jc">1,248</div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-12074" data-mp="21shwugvrfm3y">
          <div className="flex items-center gap-3 mb-2" data-id="element-12075" data-mp="21qexv7cmqin3">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg" data-id="element-12076" data-mp="26s4cewz29efx">
              <DollarSignIcon size={20} data-id="element-12077" data-mp="2ugnid5un1f4u" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-12078" data-mp="2uf2nff4evu6g">
              {t('pages.Campaigns.budget_allocated')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-12079" data-mp="2kho8jjy4zdg9">
            {t('components.ShopDashboard.usd_2')} {totalBudget.toLocaleString()}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-12080" data-mp="19318bub9gpd0">
          <div className="flex items-center gap-3 mb-2" data-id="element-12081" data-mp="1e2nmq72tsbne">
            <div className="p-2 bg-[#FEE7E5] text-[#9F140F] rounded-lg" data-id="element-12082" data-mp="1e48exq1o3ii3">
              <DollarSignIcon size={20} data-id="element-12083" data-mp="16icx7uyg509r" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-12084" data-mp="16kz3zsdijbqx">
              {t('pages.Campaigns.budget_used')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-12085" data-mp="1firnz9u6j7kn">
            {t('components.ShopDashboard.usd_2')} {totalUsed.toLocaleString()}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3 overflow-hidden" data-id="element-12086" data-mp="mv5a98r8mmk2">
            <div className="bg-[#E2231A] h-1.5 rounded-full" style={{
            width: `${totalUsed / totalBudget * 100}%`
          }} data-id="element-12087" data-mp="mssqqr5u044w"></div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-4 justify-between items-center" data-id="element-12088" data-mp="rsp075xasnru">
        <div className="relative w-full sm:w-96" data-id="element-12089" data-mp="3vb8grb6m656t">
          <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-12090" data-mp="3vafhswdwcjz8" />

          <input type="text" placeholder={t('pages.Campaigns.attrs.search_campaigns_by_name_id_or')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] focus:outline-none transition-colors" data-id="element-12091" data-mp="3eqqj5la3m4o0" />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-12092" data-mp="1inkickw8xf8">
          <span className="text-sm text-gray-500 font-medium" data-id="element-12093" data-mp="6anezpi72y4f">
            {t('pages.Campaigns.status')}
          </span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] focus:outline-none transition-colors" data-id="element-12094" data-mp="b8p00fz6l7ij">
            <option value="all" data-id="element-12095" data-mp="25r05m3dudtsn">{t('pages.Campaigns.all_statuses')}</option>
            <option value="active" data-id="element-12096" data-mp="20qvtsqnvpn8r">{t('pages.Campaigns.active')}</option>
            <option value="inactive" data-id="element-12097" data-mp="1vrspj8fq3l1u">{t('pages.Campaigns.inactive')}</option>
            <option value="expired" data-id="element-12098" data-mp="1vu57vmpe7xff">{t('pages.Campaigns.expired')}</option>
          </select>
        </div>
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12099" data-mp="2ogzou9n04eo5">
        <CampaignList campaigns={filteredCampaigns} onCampaignClick={handleCampaignClick} data-id="element-12100" data-mp="2jhd9nunm4ocp" />
      </div>
    </div>;
}